/**
 * Accessibility utilities for WCAG 2.1 AA compliance
 */

import React from 'react';

/**
 * Announce message to screen readers
 * @param message - Message to announce
 * @param priority - Priority level ('polite' or 'assertive')
 */
export const announceToScreenReader = (
  message: string,
  priority: 'polite' | 'assertive' = 'polite'
): void => {
  const announcement = document.createElement('div');
  announcement.setAttribute('role', 'status');
  announcement.setAttribute('aria-live', priority);
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;

  document.body.appendChild(announcement);

  // Remove after announcement
  setTimeout(() => {
    if (document.body.contains(announcement)) {
      document.body.removeChild(announcement);
    }
  }, 1000);
};

/**
 * Trap focus within a container (for modals)
 * @param containerRef - Ref to the container element
 * @param isActive - Whether focus trap is active
 */
export const useFocusTrap = (
  containerRef: React.RefObject<HTMLElement | null>,
  isActive: boolean
): void => {
  React.useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);
    firstElement?.focus();

    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }, [isActive, containerRef]);
};

/**
 * Get accessible label for form fields
 * @param label - Label text
 * @param required - Whether field is required
 * @param error - Error message if any
 * @returns Object with label and aria-describedby
 */
export const getAccessibleFieldProps = (
  label: string,
  required: boolean = false,
  error?: string
) => {
  const labelId = `label-${Math.random().toString(36).substr(2, 9)}`;
  const errorId = error ? `error-${Math.random().toString(36).substr(2, 9)}` : undefined;

  return {
    labelProps: {
      id: labelId,
      htmlFor: labelId,
    },
    inputProps: {
      id: labelId,
      'aria-label': required ? `${label} (required)` : label,
      'aria-required': required,
      'aria-invalid': !!error,
      'aria-describedby': error ? errorId : undefined,
    },
    errorId,
    labelText: required ? `${label} *` : label,
  };
};

/**
 * Handle keyboard navigation for custom components
 * @param onEnter - Callback for Enter key
 * @param onEscape - Callback for Escape key
 * @param onArrowUp - Callback for Arrow Up key
 * @param onArrowDown - Callback for Arrow Down key
 */
export const useKeyboardNavigation = (
  onEnter?: () => void,
  onEscape?: () => void,
  onArrowUp?: () => void,
  onArrowDown?: () => void
) => {
  return React.useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'Enter':
        case ' ':
          if (onEnter) {
            e.preventDefault();
            onEnter();
          }
          break;
        case 'Escape':
          if (onEscape) {
            e.preventDefault();
            onEscape();
          }
          break;
        case 'ArrowUp':
          if (onArrowUp) {
            e.preventDefault();
            onArrowUp();
          }
          break;
        case 'ArrowDown':
          if (onArrowDown) {
            e.preventDefault();
            onArrowDown();
          }
          break;
      }
    },
    [onEnter, onEscape, onArrowUp, onArrowDown]
  );
};

/**
 * Generate unique ID for ARIA attributes
 */
export const generateAriaId = (prefix: string = 'aria'): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};
